import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTheme } from '../contexts/ThemeContext';
import { fetchQuote } from '../api';

gsap.registerPlugin(ScrollTrigger);

export default function QuoteSection() {
  const { darkMode } = useTheme();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const quoteRef = useRef(null);
  const authorRef = useRef(null);

  const loadQuote = () => {
    setLoading(true);
    fetchQuote()
      .then(data => {
        setQuote(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching quote:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadQuote();
  }, []);

  // Extract title from Li Bai poems (first line is the title)
  const parseQuote = (quoteData) => {
    if (quoteData.author === 'Li Bai') {
      const lines = quoteData.text.split('\n');
      const title = lines[0].trim();
      const body = lines.slice(1).join('\n').trim();
      return { title, text: body };
    }
    return { title: null, text: quoteData.text };
  };

  const parsedQuote = quote ? parseQuote(quote) : { title: null, text: '' };

  useEffect(() => {
    if (!loading && quote && quoteRef.current && authorRef.current) {
      // Smoke/fade-in animation using GSAP (by word instead of character)
      const words = quoteRef.current.innerText.split(' ');
      quoteRef.current.innerHTML = words
        .map((word, i) => `<span class="quote-word" style="display: inline-block; opacity: 0; margin-right: 0.25em;">${word}</span>`)
        .join('');

      const wordElements = quoteRef.current.querySelectorAll('.quote-word');

      // Animate words appearing like smoke (triggered on scroll)
      const wordsTween = gsap.fromTo(
        wordElements,
        {
          opacity: 0,
          y: 20,
          scale: 0.8,
          filter: 'blur(10px)',
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: 0.8,
          stagger: 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: quoteRef.current,
            start: 'top 80%', // Start when top of quote is 80% down the viewport
            toggleActions: 'play none none none', // Play once, don't reverse
          },
        }
      );

      // Animate author appearing after quote
      const authorTween = gsap.fromTo(
        authorRef.current,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 0.7,
          y: 0,
          duration: 1,
          delay: words.length * 0.05 + 0.3,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: quoteRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );

      // Cleanup only this component's ScrollTriggers on unmount
      return () => {
        wordsTween.scrollTrigger?.kill();
        authorTween.scrollTrigger?.kill();
        wordsTween.kill();
        authorTween.kill();
      };
    }
  }, [loading, quote]);

  return (
    <section
      className={`w-full px-4 sm:px-6 lg:px-8 py-24 md:py-32 transition-colors duration-300 ${
        darkMode ? 'bg-black text-white' : 'bg-white text-black'
      }`}
    >
      <div className="max-w-4xl mx-auto">
        {/* Refresh Button - positioned above quote */}
        <div className="flex justify-end mb-4">
          <button
            onClick={loadQuote}
            disabled={loading}
            className={`p-2 rounded-full transition-all duration-300 ${
              loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-white/10'
            }`}
            aria-label="Load new quote"
          >
            <span className="text-2xl pb-1 bg-gradient-miami bg-200 animate-shimmer bg-clip-text text-transparent">
              ↻
            </span>
          </button>
        </div>

        {!quote || loading ? (
          <div className="min-h-[200px] flex items-center justify-center">
            <p className="text-gray-400 dark:text-gray-500 italic">
              {loading ? 'Loading...' : ''}
            </p>
          </div>
        ) : (
          <blockquote className="space-y-6 text-center">
            {parsedQuote.title && (
              <h3 className="text-lg md:text-xl font-semibold mb-4">
                {parsedQuote.title}
              </h3>
            )}
            <p
              ref={quoteRef}
              className="text-xl md:text-2xl lg:text-3xl font-light leading-relaxed italic"
            >
              {parsedQuote.text}
            </p>
            <footer
              ref={authorRef}
              className="text-sm md:text-base font-medium tracking-wider uppercase opacity-0"
            >
              — {quote.author}
            </footer>
          </blockquote>
        )}
      </div>
    </section>
  );
}
